import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Loader2, Inbox, Filter, X, Download, Archive, ArchiveRestore, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { stageClasses, priorityClasses, fmtDate, isOverdue } from "@/lib/pipeline";
import Seo from "@/components/Seo";

const selectCls = "min-h-[44px] rounded-sm border border-ei-mist bg-white px-3 py-2 text-sm text-ei-graphite focus:border-ei-navy outline-none";

export default function Enquiries() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ stage: "", priority: "", owner_id: "", product: "" });
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState([]);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (showArchived) params.archived = true;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await api.get("/enquiries", { params });
      setItems(data);
      setSelected([]);
    } finally {
      setLoading(false);
    }
  }, [q, filters, showArchived]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const hasFilters = q || Object.values(filters).some(Boolean);
  const clearAll = () => { setQ(""); setFilters({ stage: "", priority: "", owner_id: "", product: "" }); };

  const toggleOne = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (s.length === items.length ? [] : items.map((e) => e.id)));

  const runAction = async (kind) => {
    if (selected.length === 0) return;
    const n = selected.length;
    if (kind === "delete" && !window.confirm(`Permanently delete ${n} enquir${n === 1 ? "y" : "ies"}? This cannot be undone — quote history and notes go too.`)) return;
    setActing(true);
    try {
      if (kind === "delete") {
        await api.post("/enquiries/delete", { ids: selected });
        toast.success(`${n} enquir${n === 1 ? "y" : "ies"} permanently deleted`);
      } else {
        await api.post(`/enquiries/archive?archived=${kind === "archive"}`, { ids: selected });
        toast.success(kind === "archive" ? `${n} archived` : `${n} restored`);
      }
      await load();
    } catch (e) {
      toast.error("Could not update those enquiries");
    } finally {
      setActing(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (q) params.q = q;
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get("/enquiries/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `ei-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch (e) {
      toast.error("Could not export enquiries");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <Seo title="Enquiries — EI Battery Workspace" />
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <span className="ei-eyebrow">Pipeline</span>
          <h1 className="mt-1 text-2xl sm:text-3xl font-bold text-ei-navy">Enquiries</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-ei-graphite/60 tnum">{items.length} result{items.length !== 1 ? "s" : ""}</div>
          <button onClick={() => setShowArchived((v) => !v)} className={`inline-flex items-center gap-2 font-display text-sm font-bold px-4 py-2.5 rounded-sm border min-h-[44px] transition-colors ${showArchived ? "bg-ei-navy text-white border-ei-navy" : "text-ei-navy border-ei-navy/25 hover:border-ei-navy"}`} data-testid="toggle-archived">
            <Archive className="w-4 h-4" /> {showArchived ? "Viewing archived" : "Archived"}
          </button>
          <button onClick={exportCsv} disabled={exporting || items.length === 0} className="inline-flex items-center gap-2 font-display text-sm font-bold text-ei-navy border border-ei-navy/25 px-4 py-2.5 rounded-sm hover:border-ei-navy transition-colors min-h-[44px] disabled:opacity-50" data-testid="export-csv">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 bg-white border border-ei-mist rounded-md p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ei-graphite/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search business, contact, email, reference, ABN, postcode…" className="w-full min-h-[44px] rounded-sm border border-ei-mist bg-white pl-10 pr-3 py-2 text-sm focus:border-ei-navy outline-none" data-testid="enquiry-search" />
          </div>
          <div className="flex flex-wrap gap-3">
            <select value={filters.stage} onChange={(e) => setFilters((f) => ({ ...f, stage: e.target.value }))} className={selectCls} data-testid="filter-stage">
              <option value="">All stages</option>
              {meta?.stages.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} className={selectCls} data-testid="filter-priority">
              <option value="">All priorities</option>
              {meta?.priorities.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            <select value={filters.owner_id} onChange={(e) => setFilters((f) => ({ ...f, owner_id: e.target.value }))} className={selectCls} data-testid="filter-owner">
              <option value="">All owners</option>
              <option value="unassigned">Unassigned</option>
              {meta?.staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select value={filters.product} onChange={(e) => setFilters((f) => ({ ...f, product: e.target.value }))} className={selectCls} data-testid="filter-product">
              <option value="">All products</option>
              {meta?.products.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
            {hasFilters && (
              <button onClick={clearAll} className="inline-flex items-center gap-1 text-sm font-semibold text-ei-graphite/70 hover:text-ei-navy px-3 min-h-[44px]" data-testid="clear-filters">
                <X className="w-4 h-4" /> Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 bg-ei-navy text-white rounded-md px-4 py-3" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">{selected.length} selected</span>
          {showArchived ? (
            <button onClick={() => runAction("restore")} disabled={acting} className="inline-flex items-center gap-2 text-sm font-semibold border border-white/30 px-3 py-2 rounded-sm hover:bg-white/10 disabled:opacity-50" data-testid="bulk-restore">
              <ArchiveRestore className="w-4 h-4" /> Restore
            </button>
          ) : (
            <button onClick={() => runAction("archive")} disabled={acting} className="inline-flex items-center gap-2 text-sm font-semibold border border-white/30 px-3 py-2 rounded-sm hover:bg-white/10 disabled:opacity-50" data-testid="bulk-archive">
              <Archive className="w-4 h-4" /> Archive
            </button>
          )}
          <button onClick={() => runAction("delete")} disabled={acting} className="inline-flex items-center gap-2 text-sm font-semibold bg-red-700 px-3 py-2 rounded-sm hover:bg-red-800 disabled:opacity-50" data-testid="bulk-delete">
            <Trash2 className="w-4 h-4" /> Delete permanently
          </button>
          <button onClick={() => setSelected([])} className="text-sm underline text-white/80" data-testid="bulk-clear">Clear selection</button>
        </div>
      )}

      {/* Table */}
      <div className="mt-6 bg-white border border-ei-mist rounded-md overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-ei-navy" /></div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-ei-graphite/60" data-testid="enquiries-empty">
            <Inbox className="w-12 h-12 mx-auto text-ei-mist" />
            <p className="mt-4 font-semibold text-ei-navy">No enquiries found</p>
            <p className="mt-1 text-sm">{hasFilters ? "Try adjusting your filters." : "New website enquiries will appear here."}</p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden md:table w-full text-sm" data-testid="enquiries-table">
              <thead>
                <tr className="text-left border-b border-ei-mist bg-ei-white/60">
                  <th className="px-4 py-3 w-10"><input type="checkbox" checked={selected.length === items.length && items.length > 0} onChange={toggleAll} aria-label="Select all enquiries" data-testid="select-all-enquiries" /></th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Business</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Reference</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Stage</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Priority</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Owner</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Follow-up</th>
                  <th className="px-5 py-3 font-display font-semibold text-ei-navy">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ei-mist">
                {items.map((e) => (
                  <tr key={e.id} onClick={() => navigate(`/staff/enquiries/${e.id}`)} className="hover:bg-ei-white transition-colors cursor-pointer" data-testid={`enquiry-row-${e.id}`}>
                    <td className="px-4 py-4" onClick={(ev) => ev.stopPropagation()}><input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleOne(e.id)} aria-label={`Select ${e.business_name}`} data-testid={`select-enquiry-${e.id}`} /></td>
                    <td className="px-5 py-4"><Link to={`/staff/enquiries/${e.id}`} onClick={(ev) => ev.stopPropagation()} className="font-semibold text-ei-navy hover:text-ei-golddark">{e.business_name}</Link>{e.spam_suspected && <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-sm border border-amber-300 bg-amber-50 text-amber-800" data-testid={`enquiry-spam-flag-${e.id}`}>Check — autofill flag</span>}<div className="text-xs text-ei-graphite/60">{e.postcode} · {e.contact_name}</div></td>
                    <td className="px-5 py-4 tnum text-ei-graphite/80">{e.reference}</td>
                    <td className="px-5 py-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border ${stageClasses(e.stage)}`}>{e.stage}</span></td>
                    <td className="px-5 py-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-sm border ${priorityClasses(e.priority)}`}>{e.priority}</span></td>
                    <td className="px-5 py-4 text-ei-graphite/80">{e.owner_name || <span className="text-ei-graphite/40">Unassigned</span>}</td>
                    <td className="px-5 py-4"><span className={isOverdue(e.follow_up_due_at, e.stage) ? "text-red-700 font-semibold" : "text-ei-graphite/80"}>{fmtDate(e.follow_up_due_at, true)}</span></td>
                    <td className="px-5 py-4 text-ei-graphite/70">{fmtDate(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile cards */}
            <ul className="md:hidden divide-y divide-ei-mist">
              {items.map((e) => (
                <li key={e.id}>
                  <div className="flex items-start gap-3 px-4 py-4 hover:bg-ei-white">
                    <input type="checkbox" className="mt-1" checked={selected.includes(e.id)} onChange={() => toggleOne(e.id)} aria-label={`Select ${e.business_name}`} data-testid={`select-enquiry-m-${e.id}`} />
                    <Link to={`/staff/enquiries/${e.id}`} className="flex-1" data-testid={`enquiry-card-${e.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-ei-navy">{e.business_name}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border whitespace-nowrap ${stageClasses(e.stage)}`}>{e.stage}</span>
                    </div>
                    <div className="mt-1 text-xs text-ei-graphite/60 tnum">{e.reference} · {fmtDate(e.created_at)}</div>
                    {e.spam_suspected && <div className="mt-1 text-xs font-semibold text-amber-800" data-testid={`enquiry-spam-flag-m-${e.id}`}>Check — autofill flag</div>}
                    <div className="mt-2 flex items-center gap-2 text-xs">
                      <span className={`font-semibold px-2 py-0.5 rounded-sm border ${priorityClasses(e.priority)}`}>{e.priority}</span>
                      <span className="text-ei-graphite/70">{e.owner_name || "Unassigned"}</span>
                    </div>
                  </Link>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
